/* Decken und Dächer einer BSP für die Ansicht von oben bestimmen. Ohne DOM.
 * Decke: Fläche, die nach unten zeigt (auch Tunnelgewölbe und Schrägen). Dach: Fläche, die nach oben zeigt, höchstens SLAB_MAX Einheiten
 * über einer Decke liegt (Oberseite einer Platte mit einem Raum darunter) und über der nichts mehr liegt außer Himmel.
 * Böden von Zwischenebenen haben darüber eine Decke und bleiben deshalb stehen.
 * Boden im Gelände, unter dem nur ein Tunnel verläuft, ist kein Dach: Zusammenhängende Flächen in gleicher Höhe werden zusammen beurteilt,
 * und nur wenn fast alles davon über Decken liegt (ROOF_FRACTION), gilt es als Dach.
 * Zusätzlich gilt: Was nicht über dem umliegenden Gelände liegt, ist kein Dach. Das Gelände an einer Stelle ist der Median der Höhen der
 * Oberseiten ohne Decke darunter (Boden draußen und in Räumen) im Umkreis GROUND_RADIUS, gewichtet mit ihrer Fläche. Bis GROUND_MARGIN darüber
 * bleiben Oberseiten stehen, auch über Tunneln. Ein globaler Median taugt nicht: Karten mit tiefem Gelände und Gebäuden in verschiedenen Höhen. */

/** nz höchstens dieser Wert: zeigt nach unten (Wände haben 0) */
export const DOWN_NZ = -0.1;
/** nz mindestens dieser Wert: Oberseite (Böden, Dächer; keine Wände) */
export const UP_NZ = 0.3;
/** größte Dicke einer Platte zwischen Decke und Oberseite, in Einheiten */
export const SLAB_MAX = 384;
/** Anteil der Fläche eines zusammenhängenden Oberseiten-Stücks, der über Decken liegen muss, damit es als Dach gilt */
export const ROOF_FRACTION = 0.5;
/** Oberseiten höchstens so weit über dem Geländemedian zählen noch zum Gelände (Einheiten) */
export const GROUND_MARGIN = 16;
/** Umkreis (Einheiten, je Achse), in dem das Gelände bestimmt wird */
export const GROUND_RADIUS = 768;
const CELL = 128;

export interface CeilPoly { nz: number; model: number; verts: readonly (readonly number[])[]; /** Texturname; Flächen mit der Sky-Textur zählen nicht als Decke über einem Dach */ tex?: string }

interface Down { sky: boolean; verts: readonly (readonly number[])[]; x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }

const inPoly = (x: number, y: number, vs: readonly (readonly number[])[]): boolean => {
  let c = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const a = vs[i], b = vs[j];
    if ((a[1] > y) !== (b[1] > y) && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) c = !c;
  }
  return c;
};

/** Median von Werten mit Gewicht: kleinster Wert, bei dem die Summe der Gewichte die Hälfte erreicht; ohne Werte 0 */
export function areaMedian(items: readonly (readonly [number, number])[]): number {
  if (!items.length) return 0;
  const sorted = items.slice().sort((a, b) => a[0] - b[0]);
  const half = sorted.reduce((a, it) => a + it[1], 0) / 2;
  let acc = 0;
  for (const [v, w] of sorted) { acc += w; if (acc >= half) return v; }
  return sorted[sorted.length - 1][0];
}

/** Für jede Fläche: 1 = in der Übersicht ausblenden (Decke oder Dach), sonst 0.
 *  Dächer werden nur an der Welt (Modell 0) gesucht; Decken zählen in allen Modellen. */
export function ceilingMask(polys: readonly CeilPoly[], slabMax: number = SLAB_MAX): Uint8Array {
  const mask = new Uint8Array(polys.length);
  const grid = new Map<number, Down[]>();
  const key = (cx: number, cy: number): number => cx * 100003 + cy;
  polys.forEach((p, i) => {
    if (p.nz > DOWN_NZ) return;
    mask[i] = 1;
    if (p.model !== 0 || p.verts.length < 3) return;
    const xs = p.verts.map((v) => v[0]), ys = p.verts.map((v) => v[1]), zs = p.verts.map((v) => v[2]);
    const d: Down = { sky: String(p.tex || '').toUpperCase() === 'SKY', verts: p.verts, x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys), z0: Math.min(...zs), z1: Math.max(...zs) };
    for (let cx = Math.floor(d.x0 / CELL); cx <= Math.floor(d.x1 / CELL); cx++) for (let cy = Math.floor(d.y0 / CELL); cy <= Math.floor(d.y1 / CELL); cy++) {
      const k = key(cx, cy), l = grid.get(k);
      if (l) l.push(d); else grid.set(k, [d]);
    }
  });
  if (!grid.size) return mask;
  // Oberseiten (nur Welt) mit Kästchen, Höhe und Fläche
  interface Up { i: number; x0: number; x1: number; y0: number; y1: number; z: number; area: number; cand: boolean }
  const ups: Up[] = [];
  polys.forEach((p, i) => {
    if (mask[i] || p.model !== 0 || p.nz < UP_NZ || p.verts.length < 3 || String(p.tex || '').toUpperCase() === 'SKY') return;   // Himmel ist weder Dach noch Gelände
    const n = p.verts.length, xs = p.verts.map((v) => v[0]), ys = p.verts.map((v) => v[1]);
    const cx = xs.reduce((a, v) => a + v, 0) / n, cy = ys.reduce((a, v) => a + v, 0) / n, cz = p.verts.reduce((a, v) => a + v[2], 0) / n;
    let cand = false;
    const cell = grid.get(key(Math.floor(cx / CELL), Math.floor(cy / CELL)));
    if (cell) {
      const over = (d: Down): boolean => cx >= d.x0 && cx <= d.x1 && cy >= d.y0 && cy <= d.y1 && inPoly(cx, cy, d.verts);
      const under = cell.some((d) => !d.sky && d.z0 < cz && cz - d.z1 <= slabMax && over(d));   // Decke dicht darunter
      cand = under && !cell.some((d) => !d.sky && d.z1 > cz + 8 && over(d));                      // darüber noch eine Decke: Zwischenboden
    }
    let a = 0;
    for (let k = 0, j = n - 1; k < n; j = k++) a += xs[j] * ys[k] - xs[k] * ys[j];
    ups.push({ i, x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys), z: cz, area: Math.abs(a) / 2, cand });
  });
  // zusammenhängende Oberseiten gleicher Höhe zu Stücken verbinden (Kästchen berühren sich, Höhe auf 2 Einheiten gleich)
  const parent = ups.map((_, k) => k);
  const find = (k: number): number => { while (parent[k] !== k) { parent[k] = parent[parent[k]]; k = parent[k]; } return k; };
  const cells = new Map<number, number[]>();
  ups.forEach((u, k) => {
    for (let cx = Math.floor((u.x0 - 1) / CELL); cx <= Math.floor((u.x1 + 1) / CELL); cx++) for (let cy = Math.floor((u.y0 - 1) / CELL); cy <= Math.floor((u.y1 + 1) / CELL); cy++) {
      const kk = key(cx, cy), l = cells.get(kk);
      if (l) l.push(k); else cells.set(kk, [k]);
    }
  });
  cells.forEach((l) => {
    for (let a = 0; a < l.length; a++) for (let b = a + 1; b < l.length; b++) {
      const u = ups[l[a]], v = ups[l[b]];
      if (Math.abs(u.z - v.z) <= 2 && u.x0 <= v.x1 + 1 && v.x0 <= u.x1 + 1 && u.y0 <= v.y1 + 1 && v.y0 <= u.y1 + 1) parent[find(l[a])] = find(l[b]);
    }
  });
  // Gelände: Oberseiten ohne Decke darunter, in einem Raster nach Lage
  const GC = 256, gcells = new Map<number, Up[]>();
  ups.forEach((u) => {
    if (u.cand) return;
    const kk = key(Math.floor((u.x0 + u.x1) / 2 / GC), Math.floor((u.y0 + u.y1) / 2 / GC)), l = gcells.get(kk);
    if (l) l.push(u); else gcells.set(kk, [u]);
  });
  const groundAt = (x: number, y: number): number | null => {
    const r = Math.ceil(GROUND_RADIUS / GC), cx = Math.floor(x / GC), cy = Math.floor(y / GC), items: [number, number][] = [];
    for (let i = cx - r; i <= cx + r; i++) for (let j = cy - r; j <= cy + r; j++) {
      const l = gcells.get(key(i, j));
      if (l) for (const g of l) if (Math.abs((g.x0 + g.x1) / 2 - x) <= GROUND_RADIUS && Math.abs((g.y0 + g.y1) / 2 - y) <= GROUND_RADIUS) items.push([g.z, g.area]);
    }
    return items.length ? areaMedian(items) : null;
  };
  const total = new Map<number, number>(), cover = new Map<number, number>();
  ups.forEach((u, k) => { const r = find(k); total.set(r, (total.get(r) || 0) + u.area); if (u.cand) cover.set(r, (cover.get(r) || 0) + u.area); });
  ups.forEach((u, k) => {
    const r = find(k), t = total.get(r) || 0;
    if (t > 0 && (cover.get(r) || 0) / t >= ROOF_FRACTION) {
      const g = groundAt((u.x0 + u.x1) / 2, (u.y0 + u.y1) / 2);
      if (g === null || u.z > g + GROUND_MARGIN) mask[u.i] = 1;   // ohne Gelände in der Nähe: nichts spricht gegen ein Dach
    }
  });
  return mask;
}
